import { ExpoSpeechRecognitionModule } from "@-/com.npmjs.expo-speech-recognition";
import ensureError from "@-/com.npmjs.ensure-error";
import {
  buildVoiceStudyHelp,
  isVoiceStudyBargeInCommand,
  parseVoiceStudyCommand,
  type VoiceStudyCommand,
  type VoiceStudyPrompt,
  type VoiceStudySide,
} from "#voice-study-card";
import type { VoiceStudyLockScreen, VoiceStudyRemoteCommand } from "#voice-study-lock-screen";
import {
  VoiceStudySession,
  type VoiceStudyAudio,
  type VoiceStudySessionTurn,
} from "#voice-study-session";
import { VoiceStudySessionHandle } from "#voice-study-session-handle";

export type VoiceStudyTurnResult =
  | { kind: "feedback"; prompt: VoiceStudyPrompt }
  | { kind: "handled" }
  | { kind: "unhandled" };

/** What the focused card publishes: its prompt and how it maps commands onto study actions. */
export type VoiceStudyTurn = {
  /** Shown on the lock screen; never the answer. */
  glyph?: string;
  id: string;
  onCommand?: (command: VoiceStudyCommand) => Promise<VoiceStudyTurnResult>;
  prompt: VoiceStudyPrompt;
  side: VoiceStudySide;
};

/**
 * Where voice study is, as ONE value (never an `active` flag next to an `error` that could both be
 * set). Changes only on start / fail / stop, so reading it never re-renders per message.
 * - `failed`: starting failed (no recognizer, permission denied) or the session broke outside a
 *   running session; the error is shown until the user exits or starts again.
 * - A microphone lost DURING a session is not `failed`: the session continues (prompts play,
 *   typed input works) and the failure is a chat line.
 */
export type VoiceStudyLifecycle =
  | { readonly kind: "idle" }
  | { readonly kind: "starting" }
  | { readonly kind: "active"; readonly session: VoiceStudySessionHandle }
  | { readonly error: string; readonly kind: "failed" };

/** The provider's published value: replaced (new identity) only when `lifecycle` or `turn` changes. */
export type VoiceStudySnapshot = {
  readonly controller: VoiceStudyController;
  readonly lifecycle: VoiceStudyLifecycle;
  /** The focused card's turn; `null` between cards and before the first card publishes. */
  readonly turn: VoiceStudyTurn | null;
};

type VoiceStudyTurnAction =
  | VoiceStudyTurn
  | null
  | ((current: VoiceStudyTurn | null) => VoiceStudyTurn | null);

const IDLE: VoiceStudyLifecycle = { kind: "idle" };
const STARTING: VoiceStudyLifecycle = { kind: "starting" };
const UNHANDLED_PROMPT: VoiceStudyPrompt = { text: "Say help for the list of commands." };

function lockScreenMetadata(glyph: string | undefined) {
  return {
    artist: "Haniverse",
    title: glyph === undefined ? "Voice study" : `Voice study · ${glyph}`,
  };
}

/**
 * Owns voice study for the Learn stack: the lifecycle, the focused card's turn, the one
 * microphone/speaker engine, and the lock-screen carrier. Its identity is fixed for the provider's
 * lifetime, so `useVoiceStudyController()` never re-renders anyone.
 */
export class VoiceStudyController {
  private snapshot: VoiceStudySnapshot;
  private readonly listeners = new Set<() => void>();
  private readonly engine: VoiceStudySession;
  /** Bumped by every start/stop; a start that awaited across a newer request abandons itself. */
  private request = 0;
  /** The turn the engine is running, with its transcript handler; `null` while paused. */
  private sessionTurn: VoiceStudySessionTurn | null = null;

  constructor(
    audio: VoiceStudyAudio,
    private readonly lockScreen: VoiceStudyLockScreen,
    /** The app-wide mute (a nav setting, not session state), read at the moment it's needed. */
    private readonly muting: {
      readonly current: { muted: boolean; setMuted: (muted: boolean) => void };
    },
  ) {
    this.snapshot = { controller: this, lifecycle: IDLE, turn: null };
    this.engine = new VoiceStudySession(
      audio,
      (_status, reason) => {
        if (reason) {
          this.reportFailure(reason);
        }
      },
      {
        isBargeIn: isVoiceStudyBargeInCommand,
        onMessage: (message) => this.activeSession()?.appendMessage(message),
      },
    );
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): VoiceStudySnapshot => this.snapshot;

  private update(patch: { lifecycle?: VoiceStudyLifecycle; turn?: VoiceStudyTurn | null }): void {
    const lifecycle = patch.lifecycle ?? this.snapshot.lifecycle;
    const turn = patch.turn === undefined ? this.snapshot.turn : patch.turn;
    if (lifecycle === this.snapshot.lifecycle && turn === this.snapshot.turn) {
      return;
    }
    if (turn?.glyph !== this.snapshot.turn?.glyph) {
      this.lockScreen.updateMetadata(lockScreenMetadata(turn?.glyph));
    }
    this.snapshot = { controller: this, lifecycle, turn };
    for (const listener of this.listeners) {
      listener();
    }
  }

  private activeSession(): VoiceStudySessionHandle | null {
    const { lifecycle } = this.snapshot;
    return lifecycle.kind === "active" ? lifecycle.session : null;
  }

  private reportFailure(reason: Error): void {
    const { lifecycle } = this.snapshot;
    switch (lifecycle.kind) {
      case "active": {
        // The session stays up without a microphone: prompts still play and the emulated-speech
        // input still works, so surface the failure in the chat instead of pausing.
        lifecycle.session.appendMessage({
          kind: "error",
          role: "app",
          text: `Microphone unavailable: ${reason.message}`,
        });
        return;
      }
      case "starting": {
        // The start in flight reports its own outcome.
        return;
      }
      case "failed":
      case "idle": {
        this.update({ lifecycle: { error: reason.message, kind: "failed" } });
        return;
      }
    }
  }

  private createSession(): VoiceStudySessionHandle {
    const isCurrent = () => this.activeSession() === session;
    const session: VoiceStudySessionHandle = new VoiceStudySessionHandle({
      hear: (text) => {
        if (isCurrent()) {
          this.engine.hear(text);
        }
      },
      pause: () => {
        if (isCurrent()) {
          this.engine.pause();
          this.lockScreen.setPlaying(false);
        }
      },
      replay: () => {
        if (isCurrent()) {
          this.runTurn(this.snapshot.turn, this.muting.current.muted);
        }
      },
      resume: () => {
        if (isCurrent()) {
          this.lockScreen.setPlaying(true);
          this.engine.resume();
        }
      },
      say: (prompt) => (isCurrent() ? this.engine.say(prompt) : Promise.resolve()),
      stop: () => {
        if (isCurrent()) {
          this.stop();
        }
      },
    });
    return session;
  }

  /** Start a session (from `idle` or `failed`). No-op while one is starting or active. */
  readonly start = async (): Promise<void> => {
    const { kind } = this.snapshot.lifecycle;
    if (kind === "starting" || kind === "active") {
      return;
    }
    const token = ++this.request;
    this.update({ lifecycle: STARTING });
    try {
      if (!ExpoSpeechRecognitionModule.isRecognitionAvailable()) {
        throw new Error("Speech recognition is unavailable on this device.");
      }
      const permission = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
      if (token !== this.request) {
        return;
      }
      if (!permission.granted) {
        throw new Error("Allow microphone and speech recognition access in Settings to use Voice.");
      }
      await this.lockScreen.activate(lockScreenMetadata(this.snapshot.turn?.glyph));
      if (token !== this.request) {
        this.lockScreen.deactivate();
        return;
      }
      this.engine.start();
      this.update({ lifecycle: { kind: "active", session: this.createSession() } });
    } catch (error) {
      if (token === this.request) {
        this.lockScreen.deactivate();
        this.update({ lifecycle: { error: ensureError(error).message, kind: "failed" } });
      }
    }
  };

  /** End any session (or a pending start, or a shown failure). Idempotent. */
  readonly stop = (): void => {
    this.request += 1;
    this.engine.stop();
    this.lockScreen.deactivate();
    this.sessionTurn = null;
    this.update({
      lifecycle: IDLE,
      turn: this.snapshot.turn?.id === "voice-complete" ? null : this.snapshot.turn,
    });
  };

  /** Release the touch lock of the active session, if one is still running. */
  readonly unlock = (): void => {
    this.activeSession()?.setLocked(false);
  };

  /** Publish (or clear) the focused card's turn; accepts an updater like a state setter. */
  readonly setTurn = (action: VoiceStudyTurnAction): void => {
    this.update({ turn: typeof action === "function" ? action(this.snapshot.turn) : action });
  };

  /** Make `turn` the engine's current turn and speak it (unless muted). */
  runTurn(turn: VoiceStudyTurn | null, muted: boolean): void {
    const sessionTurn: VoiceStudySessionTurn | null =
      turn === null
        ? null
        : {
            id: turn.id,
            onTranscript: (text) => this.dispatchCommand(parseVoiceStudyCommand(text)),
            prompt: turn.prompt,
          };
    this.sessionTurn = sessionTurn;
    this.engine.run(sessionTurn, muted);
  }

  /** While paused the engine keeps its turn for `resume`, but remote feedback must not replay it. */
  clearSessionTurn(): void {
    this.sessionTurn = null;
  }

  private async dispatchCommand(command: VoiceStudyCommand): Promise<VoiceStudyPrompt | void> {
    const { turn } = this.snapshot;
    switch (command.kind) {
      case "stop": {
        this.stop();
        return;
      }
      case "pause": {
        this.activeSession()?.pause();
        return;
      }
      case "mute": {
        this.muting.current.setMuted(true);
        return;
      }
      case "unmute": {
        this.muting.current.setMuted(false);
        return;
      }
      case "lock": {
        this.activeSession()?.setLocked(true);
        return {
          text: "Touch locked. Say unlock, change the volume, or hold the unlock button to unlock.",
        };
      }
      case "unlock": {
        this.activeSession()?.setLocked(false);
        return { text: "Touch unlocked." };
      }
      case "repeat": {
        return turn?.prompt ?? { text: "Choose a review card to begin." };
      }
      case "help": {
        return buildVoiceStudyHelp(turn?.side ?? "front");
      }
      default: {
        if (!turn?.onCommand) {
          return;
        }
        const result = await turn.onCommand(command);
        switch (result.kind) {
          case "feedback": {
            return result.prompt;
          }
          case "handled": {
            return;
          }
          case "unhandled": {
            return UNHANDLED_PROMPT;
          }
        }
      }
    }
  }

  private dispatchRemote(command: VoiceStudyRemoteCommand): void {
    if (command === "stop") {
      this.stop();
      return;
    }
    // The lock-screen card only exists while a session is active.
    const session = this.activeSession();
    if (session === null) {
      return;
    }
    if (command === "play") {
      session.resume();
      return;
    }
    if (command === "pause") {
      session.pause();
      return;
    }
    const current = this.snapshot.turn;
    if (current === null || session.getState().paused) {
      return;
    }
    const studyCommand: VoiceStudyCommand | null =
      command === "next"
        ? current.side === "front"
          ? { kind: "reveal", text: "" }
          : { kind: "skip", text: "" }
        : current.side === "back"
          ? { kind: "unreveal", text: "" }
          : null;
    if (studyCommand === null) {
      return;
    }
    void this.dispatchCommand(studyCommand).then((feedback) => {
      const sessionTurn = this.sessionTurn;
      if (feedback !== undefined && sessionTurn !== null && sessionTurn.id === current.id) {
        this.engine.run({ ...sessionTurn, prompt: feedback }, this.muting.current.muted);
      }
    });
  }

  /** Lock-screen ▶/⏸/⏭/⏮; returns the unsubscribe. */
  subscribeRemote(): () => void {
    return this.lockScreen.onRemote((command) => this.dispatchRemote(command));
  }

  /** The provider unmounted: release the microphone, speaker and lock-screen carrier. */
  dispose(): void {
    this.request += 1;
    this.engine.stop();
    this.lockScreen.deactivate();
  }
}
